import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API, formatApiError, tokenStore } from "../../lib/api";
import { ArrowLeft, PackageCheck, PackageX, Download, AlertCircle, CheckCircle2, CheckSquare, Square, Pencil } from "lucide-react";

const REASONS = [
  { code: "COLIS_INDISPO", label: "Colis non disponibles à l'entrepôt" },
  { code: "CUISINE_NON_REGLEE", label: "Cuisine non réglée par le client" },
  { code: "AUTRE", label: "Autre motif" },
];

const STATUS_INFO = {
  COMPLETE: { label: "Récupération complète", cls: "bg-green-50 border-green-200 text-green-700" },
  PARTIAL: { label: "Récupération partielle", cls: "bg-orange-50 border-orange-200 text-orange-700" },
  FAILED: { label: "Déplacement pour rien — à facturer", cls: "bg-red-50 border-red-200 text-red-700" },
};

export default function AdminPickupMode() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [order, setOrder] = useState(null);
  const [saved, setSaved] = useState(null); // pickup saved in DB
  const [editable, setEditable] = useState(false);
  const [editing, setEditing] = useState(false);

  const [items, setItems] = useState([]);
  const [comment, setComment] = useState("");
  const [failedMode, setFailedMode] = useState(false);
  const [reasonCode, setReasonCode] = useState("COLIS_INDISPO");
  const [reasonText, setReasonText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [o, c] = await Promise.all([
          api.get(`/orders/${id}`),
          api.get(`/admin/orders/${id}/pickup-checklist`),
        ]);
        setOrder(o.data);
        setEditable(c.data.editable);
        if (c.data.saved) {
          setSaved(c.data.pickup);
        } else {
          setItems(c.data.pickup.items || []);
          setEditing(true);
        }
      } catch (e) {
        setLoadError(formatApiError(e));
        try { const o = await api.get(`/orders/${id}`); setOrder(o.data); } catch { /* noop */ }
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const startEdit = () => {
    setItems((saved.items || []).map((i) => ({ ...i })));
    setComment(saved.comment || "");
    setFailedMode(saved.status === "FAILED");
    setReasonCode(saved.reason_code || "COLIS_INDISPO");
    setReasonText(saved.reason_text || "");
    setEditing(true);
  };

  const toggle = (idx) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, checked: !it.checked } : it)));
  const setAll = (v) => setItems((arr) => arr.map((it) => ({ ...it, checked: v })));
  const picked = items.filter((i) => i.checked).length;

  const submit = async (failed) => {
    setError(null);
    setSubmitting(true);
    try {
      const r = await api.post(`/admin/orders/${id}/pickup`, {
        failed,
        items,
        reason_code: failed ? reasonCode : null,
        reason_text: failed ? reasonText : null,
        comment,
      });
      setSaved(r.data.pickup);
      setEditing(false);
      setFailedMode(false);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-[var(--lc-gray)]">Chargement…</div>;

  return (
    <div data-testid="admin-pickup-mode" className="max-w-2xl">
      <Link to={`/admin/orders/${id}`} className="text-xs text-[var(--lc-gray)] hover:text-[var(--lc-black)] inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-3 h-3" /> Retour à la commande
      </Link>
      <div className="mb-5">
        {order && <div className="font-mono text-xs text-[var(--lc-gray)]">{order.ref}</div>}
        <h1 className="display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <PackageCheck className="w-7 h-7 text-[var(--lc-orange)]" /> Récupération de la marchandise
        </h1>
      </div>

      {loadError && (
        <div data-testid="pickup-load-error" className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{loadError}</span>
        </div>
      )}
      {error && (
        <div data-testid="pickup-error" className="mb-4 flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{error}</span>
        </div>
      )}

      {saved && !editing && (
        <div className="space-y-4" data-testid="pickup-summary">
          <div className={`rounded-md border p-3 text-sm font-semibold flex items-start gap-2 ${STATUS_INFO[saved.status]?.cls || ""}`} data-testid="pickup-status-banner">
            {saved.status === "FAILED" ? <PackageX className="w-4 h-4 mt-0.5 shrink-0" /> : <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />}
            <span>
              {STATUS_INFO[saved.status]?.label}
              {saved.status === "FAILED" && (
                <span className="block font-normal mt-0.5">
                  Motif : {REASONS.find((r) => r.code === saved.reason_code)?.label || "Autre"}
                  {saved.reason_text ? ` — ${saved.reason_text}` : ""}
                </span>
              )}
            </span>
          </div>

          {saved.items?.length > 0 && (
            <div className="lc-card divide-y divide-[var(--lc-border)]">
              {saved.items.map((it, i) => (
                <div key={i} className={`flex items-center gap-3 px-4 py-2.5 text-sm ${it.checked ? "" : "text-red-600"}`}>
                  {it.checked ? <CheckSquare className="w-4 h-4 text-green-600 shrink-0" /> : <Square className="w-4 h-4 text-red-500 shrink-0" />}
                  <span className="flex-1">{it.label}</span>
                  <span className="font-mono text-xs">×{it.qty}</span>
                </div>
              ))}
            </div>
          )}
          {saved.comment && <div className="bg-[var(--lc-surface)] rounded-md p-3 text-sm"><span className="lc-small-label mr-1">Commentaire :</span>{saved.comment}</div>}
          <div className="text-xs text-[var(--lc-gray)]">
            Enregistrée le {new Date(saved.done_at).toLocaleString("fr-FR")} par {saved.done_by}
          </div>

          <div className="flex flex-wrap gap-2">
            <a
              data-testid="pickup-download-pdf"
              href={`${API}/admin/orders/${id}/pickup.pdf?token=${encodeURIComponent(tokenStore.get() || "")}`}
              target="_blank" rel="noreferrer"
              className="lc-btn-primary inline-flex items-center gap-2"
            >
              <Download className="w-4 h-4" /> PDF de récupération
            </a>
            {editable && (
              <button data-testid="pickup-edit-button" onClick={startEdit} className="lc-btn-secondary inline-flex items-center gap-2">
                <Pencil className="w-4 h-4" /> Modifier
              </button>
            )}
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-sm font-semibold" data-testid="pickup-counter">{picked}/{items.length} articles cochés</div>
            <div className="flex gap-2">
              <button data-testid="pickup-check-all" onClick={() => setAll(true)} className="lc-btn-secondary text-xs px-3 py-1.5">Tout cocher</button>
              <button data-testid="pickup-uncheck-all" onClick={() => setAll(false)} className="lc-btn-secondary text-xs px-3 py-1.5">Tout décocher</button>
            </div>
          </div>

          <div className="lc-card divide-y divide-[var(--lc-border)]" data-testid="pickup-checklist">
            {items.map((it, i) => (
              <button
                key={i}
                type="button"
                data-testid={`pickup-item-${i}`}
                onClick={() => toggle(i)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm text-left transition-colors ${it.checked ? "" : "bg-red-50/60 text-red-700"}`}
              >
                {it.checked
                  ? <CheckSquare className="w-5 h-5 text-[var(--lc-orange)] shrink-0" />
                  : <Square className="w-5 h-5 text-red-400 shrink-0" />}
                <span className="flex-1">{it.label}</span>
                <span className="font-mono text-xs text-[var(--lc-gray)]">×{it.qty}</span>
              </button>
            ))}
          </div>

          <div>
            <label className="lc-label">Commentaire (facultatif)</label>
            <textarea data-testid="pickup-comment" rows="2" className="lc-input" value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>

          {!failedMode ? (
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="pickup-submit-button"
                disabled={submitting || picked === 0}
                onClick={() => submit(false)}
                className="lc-btn-primary inline-flex items-center gap-2"
              >
                <PackageCheck className="w-4 h-4" />
                {submitting ? "Enregistrement…" : picked === items.length ? "Valider — récupération complète" : `Valider — partielle (${picked}/${items.length})`}
              </button>
              <button data-testid="pickup-failed-toggle" onClick={() => setFailedMode(true)} className="lc-btn-secondary text-red-600 border-red-300 inline-flex items-center gap-2">
                <PackageX className="w-4 h-4" /> Déplacement pour rien
              </button>
            </div>
          ) : (
            <div className="lc-card p-4 border-red-200 space-y-3" data-testid="pickup-failed-panel">
              <div className="text-sm font-bold text-red-700 flex items-center gap-2"><PackageX className="w-4 h-4" /> Déplacement pour rien — aucune marchandise récupérée</div>
              <div className="space-y-2">
                {REASONS.map((r) => (
                  <label key={r.code} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      data-testid={`pickup-reason-${r.code}`}
                      type="radio"
                      name="pickup-reason"
                      checked={reasonCode === r.code}
                      onChange={() => setReasonCode(r.code)}
                    />
                    {r.label}
                  </label>
                ))}
              </div>
              <input
                data-testid="pickup-reason-text"
                className="lc-input"
                placeholder="Précision (facultatif)"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
              />
              <div className="text-xs text-red-600 font-semibold">Le déplacement sera à facturer. Cette action sera tracée dans l'historique de la commande.</div>
              <div className="flex flex-wrap gap-2">
                <button data-testid="pickup-failed-confirm" disabled={submitting} onClick={() => submit(true)} className="lc-btn-primary bg-red-600 hover:bg-red-700 inline-flex items-center gap-2">
                  {submitting ? "Enregistrement…" : "Confirmer le déplacement pour rien"}
                </button>
                <button data-testid="pickup-failed-cancel" onClick={() => setFailedMode(false)} className="lc-btn-secondary">Annuler</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
